"use client";

import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  includeObservedRadioOption,
  observedPrimitiveSchema,
  type RadioOption,
} from './api-radio-group-options';
import { RadioGroupField } from './radio-group-field';
import { useFormModeGuard } from './use-form-mode-guard';

export type { RadioOption } from './api-radio-group-options';

const optionsSchema = z.array(z.object({
  value: z.union([z.string(), z.boolean()]),
  label: z.string(),
}));

interface ApiRadioGroupFieldProps {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  loader: () => Promise<RadioOption[]>;
  reloadKey?: string;
  onValueChange?: (value: string | boolean | undefined) => void;
}

export function ApiRadioGroupField({
  name,
  label,
  required = false,
  disabled = false,
  loader,
  reloadKey,
  onValueChange,
}: ApiRadioGroupFieldProps) {
  const { control } = useFormContext();
  const { sideEffectsAllowed } = useFormModeGuard(disabled);
  const [options, setOptions] = useState<RadioOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const observedValueResult = observedPrimitiveSchema.safeParse(useWatch({ control, name }));
  const observedLabelResult = observedPrimitiveSchema.safeParse(
    useWatch({ control, name: `${name}_label` }),
  );
  const observedValue = observedValueResult.success ? observedValueResult.data : undefined;
  const observedLabel = observedLabelResult.success ? observedLabelResult.data : undefined;
  const loaderRef = useRef(loader);
  const displayOptions = includeObservedRadioOption(options, observedValue, observedLabel);

  useEffect(() => {
    loaderRef.current = loader;
  }, [loader]);

  useEffect(() => {
    if (!sideEffectsAllowed) return;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await loaderRef.current();
        if (!mounted) return;
        const result = optionsSchema.safeParse(data);
        if (result.success) {
          setOptions(result.data);
        } else {
          setOptions([]);
          setError('La respuesta del catálogo no tiene un formato válido.');
        }
      } catch (e) {
        console.error('Error cargando opciones del API:', e);
        if (mounted) {
          setOptions([]);
          setError(e instanceof Error ? e.message : 'No se pudieron cargar las opciones.');
        }
      }
      if (!mounted) return;
      setLoading(false);
    };
    void load();
    return () => { mounted = false; };
  }, [reloadKey, retryCount, sideEffectsAllowed]);

  return (
    <>
      <RadioGroupField
        name={name}
        label={label}
        options={displayOptions}
        required={required}
        disabled={!sideEffectsAllowed || loading}
        onValueChange={onValueChange}
      />
      {sideEffectsAllowed && error ? (
        <div className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <span>{error}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={loading}
            onClick={() => setRetryCount((current) => current + 1)}
          >
            Reintentar
          </Button>
        </div>
      ) : null}
    </>
  );
}
